/**
 * Document Previous Version Hook
 *
 * Provides previous version metadata and download functionality
 * for a given document.
 */

import { api } from "convex/_generated/api";
import type { Id, DocumentId } from "@/packages/types";
import { useWorkspaceQuery } from "@/hooks";

interface PreviousVersion {
  id: string;
  documentId: DocumentId;
  versionNumber: number;
  fileName: string;
  fileType: string;
  uploadedAt: number;
  uploadedBy: string;
  archivedAt: number;
  charCount?: number;
}

interface UseDocumentPreviousVersionReturn {
  previousVersion: PreviousVersion | null;
  isLoading: boolean;
  hasPreviousVersion: boolean;
}

export function useDocumentPreviousVersion(
  documentId: Id<"documents"> | null,
): UseDocumentPreviousVersionReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getPreviousVersion,
    { documentId: documentId ?? null },
  );

  return {
    previousVersion: data ?? null,
    isLoading,
    hasPreviousVersion: !!data,
  };
}

interface UseDownloadPreviousVersionReturn {
  download: () => Promise<void>;
  isLoading: boolean;
}

export function useDownloadPreviousVersion(
  documentId: Id<"documents"> | null,
): UseDownloadPreviousVersionReturn {
  const { data, isLoading } = useWorkspaceQuery(
    api.document_queries.getPreviousVersionDownloadUrl,
    documentId ? { documentId } : "skip",
  );

  async function download() {
    const url = data?.url;
    const fileName = data?.fileName;
    if (!url || !fileName) return;

    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    } catch {
      if (url) window.open(url, "_blank");
    }
  }

  return { download, isLoading };
}
